import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { milestoneStatusOptions } from './constants';
import type { MilestoneStatus } from './constants';
import type { Tables } from '@/types/database.types';

type MilestoneRow = Tables<'milestones'>;

export function MilestoneItem({
  milestone,
  onUpdate,
  onDelete,
  readOnly = false
}: {
  milestone: MilestoneRow;
  onUpdate: (id: string, updates: Partial<{ name: string; dueDate: string | null; status: MilestoneStatus; ownerName: string | null }>) => void;
  onDelete: (id: string) => void;
  readOnly?: boolean;
}) {
  const [name, setName] = useState(milestone.name);
  const [dueDate, setDueDate] = useState(milestone.due_date || '');
  const [status, setStatus] = useState<MilestoneStatus>(milestone.status as MilestoneStatus);
  const [ownerName, setOwnerName] = useState(milestone.owner_name || '');

  const debouncedName = useDebounce(name, 800);
  const debouncedOwnerName = useDebounce(ownerName, 800);
  const initialRef = useRef(true);

  useEffect(() => {
    if (!initialRef.current && debouncedName !== milestone.name) {
      onUpdate(milestone.id, { name: debouncedName });
    }
    initialRef.current = false;
  }, [debouncedName, milestone.id, milestone.name, onUpdate]);

  useEffect(() => {
    if (debouncedOwnerName !== (milestone.owner_name || '')) {
      onUpdate(milestone.id, { ownerName: debouncedOwnerName || null });
    }
  }, [debouncedOwnerName, milestone.id, milestone.owner_name, onUpdate]);

  const handleDateChange = (newDate: string) => {
    setDueDate(newDate);
    onUpdate(milestone.id, { dueDate: newDate || null });
  };

  const handleStatusChange = (newStatus: MilestoneStatus) => {
    setStatus(newStatus);
    onUpdate(milestone.id, { status: newStatus });
  };

  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg group">
      <div className="flex-1 space-y-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="font-medium border-0 bg-transparent px-0 h-auto focus-visible:ring-0"
          placeholder="Milestone name"
          readOnly={readOnly}
        />
        <div className="flex gap-2 items-center flex-wrap">
          <Input
            type="date"
            value={dueDate}
            onChange={(e) => handleDateChange(e.target.value)}
            className="w-36 text-sm h-8"
            readOnly={readOnly}
          />
          <Select
            value={status}
            onChange={(e) => handleStatusChange(e.target.value as MilestoneStatus)}
            className={cn("h-8 text-sm", readOnly && "cursor-not-allowed opacity-60")}
            disabled={readOnly}
          >
            {milestoneStatusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
          <Input
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="Owner"
            className="w-32 text-sm h-8"
            readOnly={readOnly}
          />
        </div>
      </div>
      {!readOnly && (
        <button
          onClick={() => onDelete(milestone.id)}
          className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
